import {
  type RovoContentBridgeAPI,
  type client,
} from '@atlassian/rovo-content-bridge-api';

import {
  PointerDownCommand,
  PointerMoveCommand,
  PointerUpCommand,
} from '@canvas/databases-bridge-api/commands';

type ForwardPointerEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

export function setupPointerEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardPointerEventsParams): () => void {
  let isForwarding = false;

  const handlePointerDown = (event: PointerEvent) => {
    const target = getElementFromEventTarget(event.target);
    if (!target) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    isForwarding = true;

    bridge.send(PointerDownCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event, isInteractiveDbElement),
    });
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!isForwarding) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    bridge.send(PointerMoveCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event, isInteractiveDbElement),
    });
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (!isForwarding) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    bridge.send(PointerUpCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event, isInteractiveDbElement),
    });

    isForwarding = false;
  };

  const eventOptions = {
    passive: false,
    capture: true,
  };

  document.addEventListener('pointerdown', handlePointerDown, eventOptions);
  document.addEventListener('pointermove', handlePointerMove, eventOptions);
  document.addEventListener('pointerup', handlePointerUp, eventOptions);

  return () => {
    document.removeEventListener('pointerdown', handlePointerDown, eventOptions);
    document.removeEventListener('pointermove', handlePointerMove, eventOptions);
    document.removeEventListener('pointerup', handlePointerUp, eventOptions);
  };
}

const getElementFromEventTarget = (target: EventTarget | null): Element | null => {
  if (!target) {
    return null;
  }

  // If the target is a text node (e.g. `<div>Hello</div>`) return the parent `div` element
  if (target instanceof Text) {
    return target.parentElement;
  }

  if (target instanceof Element) {
    return target;
  }

  return null;
};

const hasInteractiveAttributes = (element: Element): boolean => {
  const tagName = element.tagName;
  if (
    tagName === 'BUTTON' ||
    tagName === 'INPUT' ||
    tagName === 'A' ||
    tagName === 'SELECT' ||
    tagName === 'TEXTAREA'
  ) {
    return true;
  }

  // This attribute typically decorates dropdowns and other interactive elements
  if (element.hasAttribute('data-interactive')) {
    return true;
  }

  // draggable=true elements are interactive and should be skipped
  if (element.getAttribute('draggable') === 'true') {
    return true;
  }

  const contentEditable = element.getAttribute('contenteditable');
  if (contentEditable === 'true' || contentEditable === '') {
    return true;
  }

  const role = element.getAttribute('role');
  if (role === 'button' || role === 'tab' || role === 'link') {
    return true;
  }

  return false;
};

const isInteractiveElement = (event: PointerEvent): boolean => {
  const target = getElementFromEventTarget(event.target);

  let current: Element | null = target;

  // Traverse up the DOM tree until we find an interactive element or reach the body
  while (current !== null && current !== document.body) {
    if (hasInteractiveAttributes(current)) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};

const createEventProperties = (event: PointerEvent, isInteractiveDbElement = false) => ({
  clientX: event.clientX,
  clientY: event.clientY,
  button: event.button,
  buttons: event.buttons,
  pointerId: event.pointerId,
  pointerType: event.pointerType,
  viewportWidth: document.documentElement.clientWidth,
  viewportHeight: document.documentElement.clientHeight,
  ctrlKey: event.ctrlKey,
  shiftKey: event.shiftKey,
  altKey: event.altKey,
  metaKey: event.metaKey,
  isInteractiveDbElement,
});
